import { useState, useRef, useCallback } from 'react';
import { X, Upload, Trash2, AlertCircle, CheckCircle } from 'react-feather';
import { toast } from 'react-toastify';
import { postData, ImageFileUpload } from '../../utility/Utility';

const LeadQuotationModal = ({ show, onClose, leadId, onSuccess }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    // Check if file is PDF
    const isPdf = useCallback((file) => {
        return file && file.type === 'application/pdf';
    }, []);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        processFiles(selectedFiles);
    };

    const processFiles = (fileList) => {
        const validFiles = [];
        const invalidFiles = [];

        fileList.forEach(file => {
            if (isPdf(file)) {
                validFiles.push({
                    file,
                    id: `${file.name}-${file.lastModified}`,
                    name: file.name,
                    status: 'pending'
                });
            } else {
                invalidFiles.push(file.name);
            }
        });

        if (invalidFiles.length > 0) {
            setError(`The following files are not valid PDFs: ${invalidFiles.join(', ')}`);
        } else {
            setError('');
        }

        if (validFiles.length > 0) {
            setFiles(prevFiles => [...prevFiles, ...validFiles]);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        processFiles(droppedFiles);
    };

    const removeFile = (fileId) => {
        setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (files.length === 0) {
            setError('Please select at least one PDF file to upload');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const formData = new FormData();
            files.forEach((fileObj) => {
                formData.append('files', fileObj.file);
            });

            const config = {
                headers: {
                    'is_upload': 's3'
                }
            };

            const response = await ImageFileUpload('api/admin/upload-pdf', formData, config);

            if (response?.success) {
                const payload = {
                    enquiryId: leadId,
                    quotationPdf: response?.data?.url || response?.url // Handle both response formats
                };

                // Update the enquiry with the quotation URL
                const updateResponse = await postData("api/enquiries/update", payload);

                if (updateResponse?.success) {
                    toast.success(`Successfully uploaded ${files.length} quotation(s) and updated enquiry`);
                    onSuccess?.(response.data || []);
                    setFiles([]); // Clear the files state
                    onClose();
                } else {
                    throw new Error(updateResponse?.message || 'Failed to update enquiry with quotation URL');
                }
            } else {
                throw new Error(response?.message || 'Failed to upload one or more quotations');
            }
        } catch (error) {
            console.error('Error uploading quotations:', error);
            const errorMessage = error.response?.data?.message || 'Failed to upload quotations';
            toast.error(errorMessage);

            // Update file statuses to show error
            setFiles(prevFiles =>
                prevFiles.map(f => ({
                    ...f,
                    status: 'error',
                    error: errorMessage
                }))
            );
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px' }}>
                <div className="modal-content">
                    <div className="modal-header bg-light">
                        <h5 className="modal-title d-flex align-items-center">
                            <i className="mdi mdi-file-pdf-box me-2 text-danger"></i>
                            Upload Quotation PDFs
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={loading}
                            aria-label="Close"
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div
                                className={`dropzone p-4 text-center border-2 border-dashed ${error ? 'border-danger' : 'border-primary'}`}
                                style={{
                                    borderRadius: '10px',
                                    backgroundColor: '#f8f9fa',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    borderStyle: 'dashed',
                                    borderWidth: '2px',
                                    borderColor: error ? '#dc3545' : '#3b5de7',
                                    '&:hover': {
                                        backgroundColor: '#f1f3ff',
                                        borderColor: '#2a4edb'
                                    }
                                }}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onClick={() => fileInputRef.current?.click()}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="text-center mb-3" style={{ color: '#6c757d' }}>
                                    <i className="mdi mdi-cloud-upload" style={{ fontSize: '48px', marginBottom: '8px', color: '#3b5de7' }}></i>
                                </div>
                                <h5 style={{ color: '#343a40', marginBottom: '8px' }}>Drag & drop your PDF here</h5>
                                <p style={{ color: '#6c757d', marginBottom: '16px' }}>or click to browse files</p>

                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="d-none"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    multiple
                                />

                                {files.length > 0 && (
                                    <div className="mt-3">
                                        {files.map((fileObj) => (
                                            <div key={fileObj.id} className="d-flex align-items-center justify-content-between p-3 mb-2"
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '8px',
                                                    borderLeft: '4px solid #3b5de7',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'translateX(4px)'
                                                    }
                                                }}>
                                                <div className="d-flex align-items-center">
                                                    <i className="mdi mdi-file-pdf-box text-danger me-2" style={{ fontSize: '20px' }}></i>
                                                    <div className="text-truncate" style={{ maxWidth: '200px' }} title={fileObj.name}>
                                                        <div className="small text-dark">{fileObj.name}</div>
                                                        <div className="small text-muted">{(fileObj.file.size / 1024).toFixed(1)} KB</div>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 ms-2"
                                                    style={{ color: '#dc3545' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeFile(fileObj.id);
                                                    }}
                                                    disabled={loading}
                                                    title="Remove file"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {error && (
                                    <div className="alert alert-danger d-flex align-items-center mt-3 mb-0">
                                        <AlertCircle className="flex-shrink-0 me-2" size={18} />
                                        <div>{error}</div>
                                    </div>
                                )}
                                {!error && files.length > 0 && (
                                    <div className="alert alert-success d-flex align-items-center mt-3 mb-0">
                                        <CheckCircle className="flex-shrink-0 me-2" size={18} />
                                        <div>{files.length} file{files.length > 1 ? 's' : ''} selected</div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 text-muted small text-center">
                                <p className="mb-1"><i className="mdi mdi-information-outline me-1"></i> Supported format: .pdf</p>
                                <p className="mb-1"><i className="mdi mdi-file-document-outline me-1"></i> Max file size: 10MB per file</p>
                                <p className="mb-0"><i className="mdi mdi-numeric-5-box-multiple-outline me-1"></i> Max files: 5 at a time</p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={onClose}
                                disabled={loading}
                            >
                                <i className="mdi mdi-close me-1"></i>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading || files.length === 0}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <i className="mdi mdi-cloud-upload me-1"></i>
                                        {files.length > 1 ? `Upload ${files.length} Files` : 'Upload File'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Default props for the component
LeadQuotationModal.defaultProps = {
    maxFiles: 5,
    onSuccess: () => { }
};

export default LeadQuotationModal;
